import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import api from '../../utils/api';
import { toast } from 'react-toastify';
import { 
  Calendar, 
  Filter, 
  RefreshCw, 
  ExternalLink, 
  AlertCircle, 
  Clock, 
  User, 
  ChevronDown, 
  ChevronRight,
  TrendingUp,
  Download,
  ZoomIn,
  ZoomOut
} from 'lucide-react';
import moment from 'moment';

const GanttChart = () => {
  const { user } = useSelector((state) => state.auth);
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: '',
    priority: '',
    assignedTo: '',
    facility: '',
    caseType: '',
    dateRange: '30',
    groupBy: 'none' // none, investigator, priority, status, facility
  });
  const [dateRange, setDateRange] = useState({
    start: moment().subtract(30, 'days').toDate(),
    end: moment().add(30, 'days').toDate()
  });
  const [selectedCases, setSelectedCases] = useState([]);
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [viewMode, setViewMode] = useState('week'); // day, week, month
  const [zoomLevel, setZoomLevel] = useState(1);

  const fetchCases = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        limit: 200,
        isArchived: false
      };

      if (filters.status) params.status = filters.status;
      if (filters.priority) params.priority = filters.priority;
      if (filters.assignedTo) params.assignedTo = filters.assignedTo;

      const response = await api.get('/cases', { params });
      let fetchedCases = response.data.data || [];

      // Populate assignedTo if not already populated
      for (let i = 0; i < fetchedCases.length; i++) {
        if (fetchedCases[i].assignedTo && typeof fetchedCases[i].assignedTo === 'string') {
          try {
            const userResponse = await api.get(`/users/${fetchedCases[i].assignedTo}`);
            fetchedCases[i].assignedTo = userResponse.data.data;
          } catch (e) {
            // Skip if user fetch fails
          }
        }
      }

      // Filter by date range
      const filteredCases = fetchedCases.filter(c => {
        const createdDate = moment(c.createdAt);
        const dueDate = c.dueDate ? moment(c.dueDate) : null;
        const closedDate = c.closedDate ? moment(c.closedDate) : null;
        
        return (
          createdDate.isBetween(moment(dateRange.start), moment(dateRange.end), null, '[]') ||
          (dueDate && dueDate.isBetween(moment(dateRange.start), moment(dateRange.end), null, '[]')) ||
          (closedDate && closedDate.isBetween(moment(dateRange.start), moment(dateRange.end), null, '[]'))
        );
      });

      setCases(filteredCases);
    } catch (error) {
      console.error('Error fetching cases:', error);
      toast.error('Failed to load cases');
    } finally {
      setLoading(false);
    }
  }, [filters, dateRange]);

  useEffect(() => {
    fetchCases();
  }, [fetchCases]);

  const updateDateRange = (days) => {
    setDateRange({
      start: moment().subtract(parseInt(days), 'days').toDate(),
      end: moment().add(parseInt(days), 'days').toDate()
    });
    setFilters({ ...filters, dateRange: days });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Open':
        return { bg: 'bg-green-500', border: 'border-green-600', text: 'text-green-800' };
      case 'Initial Review':
        return { bg: 'bg-yellow-500', border: 'border-yellow-600', text: 'text-yellow-800' };
      case 'Ongoing':
        return { bg: 'bg-blue-500', border: 'border-blue-600', text: 'text-blue-800' };
      case 'Closed – No Case':
        return { bg: 'bg-gray-500', border: 'border-gray-600', text: 'text-gray-800' };
      case 'Closed – Case Created':
        return { bg: 'bg-green-500', border: 'border-green-600', text: 'text-green-800' };
      default:
        return { bg: 'bg-gray-400', border: 'border-gray-500', text: 'text-gray-800' };
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'Urgent':
        return { bg: 'bg-red-100', border: 'border-red-500', text: 'text-red-800', dot: 'bg-red-500' };
      case 'High':
        return { bg: 'bg-orange-100', border: 'border-orange-500', text: 'text-orange-800', dot: 'bg-orange-500' };
      case 'Medium':
        return { bg: 'bg-yellow-100', border: 'border-yellow-500', text: 'text-yellow-800', dot: 'bg-yellow-500' };
      case 'Low':
        return { bg: 'bg-green-100', border: 'border-green-500', text: 'text-green-800', dot: 'bg-green-500' };
      default:
        return { bg: 'bg-gray-100', border: 'border-gray-500', text: 'text-gray-800', dot: 'bg-gray-500' };
    }
  };

  const isOverdue = (dueDate, status) => {
    if (!dueDate || status === 'Closed – No Case' || status === 'Closed – Case Created') return false;
    return moment(dueDate).isBefore(moment(), 'day');
  };

  const calculateBarPosition = (caseItem) => {
    const startDate = moment(caseItem.createdAt);
    const endDate = caseItem.dueDate 
      ? moment(caseItem.dueDate) 
      : caseItem.closedDate 
        ? moment(caseItem.closedDate)
        : moment().add(7, 'days');
    
    const chartStart = moment(dateRange.start);
    const chartEnd = moment(dateRange.end);
    const totalDays = chartEnd.diff(chartStart, 'days');
    
    const daysFromStart = Math.max(0, startDate.diff(chartStart, 'days'));
    const barWidth = Math.max(1, endDate.diff(startDate, 'days'));
    
    const leftPercent = totalDays > 0 ? (daysFromStart / totalDays) * 100 : 0;
    const widthPercent = totalDays > 0 ? (barWidth / totalDays) * 100 : 0;
    
    return {
      left: `${Math.min(100, Math.max(0, leftPercent))}%`,
      width: `${Math.min(100, widthPercent)}%`,
      startDate: startDate.format('MMM D, YYYY'),
      endDate: endDate.format('MMM D, YYYY'),
      daysFromStart,
      barWidth
    };
  };

  const toggleCaseSelection = (caseId) => {
    setSelectedCases(prev => 
      prev.includes(caseId) 
        ? prev.filter(id => id !== caseId)
        : [...prev, caseId]
    );
  };

  const toggleGroup = (groupKey) => {
    setExpandedGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(groupKey)) {
        newSet.delete(groupKey);
      } else {
        newSet.add(groupKey);
      }
      return newSet;
    });
  };

  const generateDateHeaders = () => {
    const headers = [];
    const start = moment(dateRange.start);
    const end = moment(dateRange.end);
    
    if (viewMode === 'day') {
      let current = start.clone();
      while (current.isSameOrBefore(end, 'day')) {
        headers.push(current.clone());
        current.add(1, 'day');
      }
    } else if (viewMode === 'week') {
      let current = start.clone().startOf('week');
      while (current.isSameOrBefore(end, 'week')) {
        headers.push(current.clone());
        current.add(1, 'week');
      }
    } else {
      let current = start.clone().startOf('month');
      while (current.isSameOrBefore(end, 'month')) {
        headers.push(current.clone());
        current.add(1, 'month');
      }
    }
    
    return headers;
  };

  const groupCases = () => {
    if (filters.groupBy === 'none') {
      return { 'All Cases': cases };
    }

    const grouped = {};
    cases.forEach(caseItem => {
      let key = 'Unassigned';
      
      if (filters.groupBy === 'investigator') {
        key = caseItem.assignedTo 
          ? `${caseItem.assignedTo.firstName || ''} ${caseItem.assignedTo.lastName || ''}`.trim() || 'Unassigned'
          : 'Unassigned';
      } else if (filters.groupBy === 'priority') {
        key = caseItem.priority || 'Unassigned';
      } else if (filters.groupBy === 'status') {
        key = caseItem.status || 'Unassigned';
      } else if (filters.groupBy === 'facility') {
        key = caseItem.facility?.name || 'Unassigned';
      }

      if (!grouped[key]) {
        grouped[key] = [];
      }
      grouped[key].push(caseItem);
    });

    return grouped;
  };

  const groupedCases = groupCases();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Case Timeline (Gantt Chart)</h1>
          <p className="text-gray-600 mt-1">Track case progression, deadlines, and investigator workload</p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={fetchCases}
            className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Filters and Controls */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <Filter className="w-5 h-5 text-gray-500" />
            <h2 className="text-lg font-semibold text-gray-900">Filters & View Options</h2>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setZoomLevel(prev => Math.max(0.5, prev - 0.1))}
              className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              title="Zoom Out"
            >
              <ZoomOut className="w-4 h-4" />
            </button>
            <button
              onClick={() => setZoomLevel(prev => Math.min(2, prev + 0.1))}
              className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              title="Zoom In"
            >
              <ZoomIn className="w-4 h-4" />
            </button>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
            >
              <option value="">All Statuses</option>
              <option value="Open">Open</option>
              <option value="Initial Review">Initial Review</option>
              <option value="Ongoing">Ongoing</option>
              <option value="Closed – No Case">Closed – No Case</option>
              <option value="Closed – Case Created">Closed – Case Created</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Priority</label>
            <select
              value={filters.priority}
              onChange={(e) => setFilters({ ...filters, priority: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
            >
              <option value="">All Priorities</option>
              <option value="Urgent">Urgent</option>
              <option value="High">High</option>
              <option value="Medium">Medium</option>
              <option value="Low">Low</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Date Range</label>
            <select
              value={filters.dateRange}
              onChange={(e) => updateDateRange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
            >
              <option value="7">Last 7 days + Next 7 days</option>
              <option value="30">Last 30 days + Next 30 days</option>
              <option value="60">Last 60 days + Next 60 days</option>
              <option value="90">Last 90 days + Next 90 days</option>
              <option value="180">Last 180 days + Next 180 days</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">View Mode</label>
            <select
              value={viewMode}
              onChange={(e) => setViewMode(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
            >
              <option value="day">Day</option>
              <option value="week">Week</option>
              <option value="month">Month</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Group By</label>
            <select
              value={filters.groupBy}
              onChange={(e) => setFilters({ ...filters, groupBy: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
            >
              <option value="none">No Grouping</option>
              <option value="investigator">Investigator</option>
              <option value="priority">Priority</option>
              <option value="status">Status</option>
              <option value="facility">Facility</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Assigned To</label>
            <select
              value={filters.assignedTo}
              onChange={(e) => setFilters({ ...filters, assignedTo: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
            >
              <option value="">All Investigators</option>
              {user?.role === 'Administrator' || user?.role === 'Supervisor' ? (
                <option value={user.id || user._id}>My Cases</option>
              ) : null}
            </select>
          </div>
        </div>
      </div>

      {/* Gantt Chart */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="p-4 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">Case Timeline</h2>
            <div className="flex items-center space-x-4 text-sm text-gray-600">
              <span>Showing {cases.length} case{cases.length !== 1 ? 's' : ''}</span>
              {selectedCases.length > 0 && (
                <span className="text-blue-600 font-medium">
                  {selectedCases.length} selected
                </span>
              )}
            </div>
          </div>
        </div>

        {cases.length === 0 ? (
          <div className="p-12 text-center">
            <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">No cases found for the selected filters</p>
          </div>
        ) : (
          <div className="overflow-x-auto" style={{ transform: `scale(${zoomLevel})`, transformOrigin: 'top left' }}>
            {/* Date Headers */}
            <div className="border-b-2 border-gray-300 bg-gray-50 sticky top-0 z-20">
              <div className="flex" style={{ minWidth: `${1200 * zoomLevel}px` }}>
                <div className="w-72 border-r-2 border-gray-300 p-3 font-semibold text-gray-700 bg-white">
                  Case Information
                </div>
                <div className="flex-1 flex">
                  {generateDateHeaders().map((date, index) => {
                    const isToday = date.isSame(moment(), viewMode === 'day' ? 'day' : viewMode === 'week' ? 'week' : 'month');
                    return (
                      <div
                        key={index}
                        className={`flex-1 border-r border-gray-200 p-2 text-center text-xs ${
                          isToday ? 'bg-blue-50 font-semibold' : ''
                        }`}
                        style={{ minWidth: `${100 * zoomLevel}px` }}
                      >
                        <div className={`font-medium ${isToday ? 'text-blue-700' : 'text-gray-700'}`}>
                          {viewMode === 'day' 
                            ? date.format('MMM D')
                            : viewMode === 'week'
                            ? `${date.format('MMM D')} - ${date.clone().endOf('week').format('MMM D')}`
                            : date.format('MMMM YYYY')
                          }
                        </div>
                        {viewMode !== 'month' && (
                          <div className="text-gray-500 text-xs mt-1">{date.format('ddd')}</div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Case Rows - Grouped */}
            <div className="divide-y divide-gray-200">
              {Object.entries(groupedCases).map(([groupKey, groupCases]) => {
                const isExpanded = expandedGroups.has(groupKey);
                const groupCount = groupCases.length;
                
                return (
                  <div key={groupKey}>
                    {/* Group Header */}
                    {filters.groupBy !== 'none' && (
                      <div
                        className="bg-gray-100 hover:bg-gray-200 transition-colors cursor-pointer border-b border-gray-300"
                        onClick={() => toggleGroup(groupKey)}
                      >
                        <div className="flex items-center px-4 py-3">
                          {isExpanded ? (
                            <ChevronDown className="w-4 h-4 text-gray-600 mr-2" />
                          ) : (
                            <ChevronRight className="w-4 h-4 text-gray-600 mr-2" />
                          )}
                          <span className="font-semibold text-gray-900">{groupKey}</span>
                          <span className="ml-2 text-sm text-gray-600">({groupCount} cases)</span>
                        </div>
                      </div>
                    )}

                    {/* Group Cases */}
                    {isExpanded || filters.groupBy === 'none' ? (
                      groupCases.map((caseItem) => {
                        const barPosition = calculateBarPosition(caseItem);
                        const caseId = caseItem.id || caseItem._id;
                        const isSelected = selectedCases.includes(caseId);
                        const statusColors = getStatusColor(caseItem.status);
                        const priorityColors = getPriorityColor(caseItem.priority);
                        const overdue = isOverdue(caseItem.dueDate, caseItem.status);
                        const assignedToName = caseItem.assignedTo 
                          ? `${caseItem.assignedTo.firstName || ''} ${caseItem.assignedTo.lastName || ''}`.trim()
                          : 'Unassigned';

                        return (
                          <div
                            key={caseId}
                            className={`flex items-center hover:bg-gray-50 transition-colors border-b border-gray-100 ${
                              isSelected ? 'bg-blue-50' : ''
                            } ${overdue ? 'bg-red-50/30' : ''}`}
                            style={{ minWidth: `${1200 * zoomLevel}px` }}
                          >
                            {/* Case Info Column */}
                            <div className="w-72 border-r border-gray-200 p-3 flex items-center space-x-3 bg-white">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => toggleCaseSelection(caseId)}
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center space-x-2 mb-1">
                                  <Link
                                    to={`/cases/${caseId}`}
                                    className="text-sm font-semibold text-blue-600 hover:text-blue-700 flex items-center space-x-1"
                                  >
                                    <span>{caseItem.caseNumber}</span>
                                    <ExternalLink className="w-3 h-3" />
                                  </Link>
                                  {overdue && (
                                    <AlertCircle className="w-4 h-4 text-red-500" title="Overdue" />
                                  )}
                                </div>
                                <p className="text-xs text-gray-600 truncate mb-2">{caseItem.title}</p>
                                
                                {/* Priority Badge */}
                                <div className="flex items-center space-x-2 mb-2">
                                  <span className={`px-2 py-0.5 rounded text-xs font-medium border ${priorityColors.bg} ${priorityColors.border} ${priorityColors.text}`}>
                                    <span className={`inline-block w-1.5 h-1.5 rounded-full ${priorityColors.dot} mr-1`}></span>
                                    {caseItem.priority}
                                  </span>
                                  {caseItem.isHighProfile && (
                                    <span className="px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800 border border-yellow-300">
                                      High-Profile
                                    </span>
                                  )}
                                </div>

                                {/* Investigator & Due Date */}
                                <div className="space-y-1">
                                  <div className="flex items-center space-x-1 text-xs text-gray-600">
                                    <User className="w-3 h-3" />
                                    <span className="truncate">{assignedToName}</span>
                                  </div>
                                  {caseItem.dueDate && (
                                    <div className={`flex items-center space-x-1 text-xs ${
                                      overdue ? 'text-red-600 font-semibold' : 'text-gray-600'
                                    }`}>
                                      <Clock className="w-3 h-3" />
                                      <span>Due: {moment(caseItem.dueDate).format('MMM D, YYYY')}</span>
                                    </div>
                                  )}
                                  {caseItem.facility?.name && (
                                    <div className="text-xs text-gray-500 truncate">
                                      📍 {caseItem.facility.name}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>

                            {/* Timeline Bar Column */}
                            <div className="flex-1 relative h-20 flex items-center">
                              <div className="absolute inset-0 flex items-center">
                                {/* Background grid lines */}
                                {generateDateHeaders().map((date, index) => {
                                  const isToday = date.isSame(moment(), viewMode === 'day' ? 'day' : viewMode === 'week' ? 'week' : 'month');
                                  return (
                                    <div
                                      key={index}
                                      className={`absolute border-l h-full ${
                                        isToday ? 'border-blue-400 border-dashed' : 'border-gray-200'
                                      }`}
                                      style={{ left: `${(index / generateDateHeaders().length) * 100}%` }}
                                    />
                                  );
                                })}
                                
                                {/* Case timeline bar */}
                                <div
                                  className={`absolute h-10 rounded-lg ${statusColors.bg} border-2 ${statusColors.border} opacity-90 hover:opacity-100 transition-all cursor-pointer shadow-md hover:shadow-lg group`}
                                  style={{
                                    left: barPosition.left,
                                    width: barPosition.width,
                                    minWidth: '40px'
                                  }}
                                  title={`${caseItem.caseNumber}: ${barPosition.startDate} - ${barPosition.endDate}\nStatus: ${caseItem.status}\nPriority: ${caseItem.priority}`}
                                >
                                  <div className="h-full flex items-center px-2 text-white text-xs font-medium">
                                    <span className="truncate">{caseItem.caseNumber}</span>
                                  </div>
                                  
                                  {/* Progress indicator for Ongoing cases */}
                                  {caseItem.status === 'Ongoing' && caseItem.dueDate && (
                                    <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/30 rounded-b-lg">
                                      <div 
                                        className="h-full bg-white/60 rounded-b-lg"
                                        style={{
                                          width: `${Math.min(100, (moment().diff(moment(caseItem.createdAt), 'days') / moment(caseItem.dueDate).diff(moment(caseItem.createdAt), 'days')) * 100)}%`
                                        }}
                                      />
                                    </div>
                                  )}
                                </div>

                                {/* Deadline marker */}
                                {caseItem.dueDate && (
                                  (() => {
                                    const totalDays = moment(dateRange.end).diff(moment(dateRange.start), 'days');
                                    const daysFromStart = moment(caseItem.dueDate).diff(moment(dateRange.start), 'days');
                                    const deadlinePercent = totalDays > 0 ? (daysFromStart / totalDays) * 100 : 0;
                                    const isInRange = deadlinePercent >= 0 && deadlinePercent <= 100;
                                    
                                    if (isInRange) {
                                      return (
                                        <div
                                          className={`absolute w-1 h-full z-10 ${overdue ? 'bg-red-600' : 'bg-orange-500'}`}
                                          style={{
                                            left: `${Math.max(0, Math.min(100, deadlinePercent))}%`
                                          }}
                                          title={`Deadline: ${moment(caseItem.dueDate).format('MMM D, YYYY')}${overdue ? ' (OVERDUE)' : ''}`}
                                        />
                                      );
                                    }
                                    return null;
                                  })()
                                )}

                                {/* Today indicator */}
                                {(() => {
                                  const totalDays = moment(dateRange.end).diff(moment(dateRange.start), 'days');
                                  const daysFromStart = moment().diff(moment(dateRange.start), 'days');
                                  const todayPercent = totalDays > 0 ? (daysFromStart / totalDays) * 100 : 0;
                                  const isInRange = todayPercent >= 0 && todayPercent <= 100;
                                  
                                  if (isInRange) {
                                    return (
                                      <div
                                        className="absolute w-0.5 h-full bg-red-500 z-20"
                                        style={{
                                          left: `${Math.max(0, Math.min(100, todayPercent))}%`
                                        }}
                                        title="Today"
                                      />
                                    );
                                  }
                                  return null;
                                })()}
                              </div>
                            </div>
                          </div>
                        );
                      })
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Legend */}
        <div className="p-4 border-t border-gray-200 bg-gray-50">
          <div className="flex flex-wrap items-center gap-6 text-xs">
            <div className="flex items-center space-x-3">
              <span className="font-semibold text-gray-700">Status:</span>
              {['Initial Review', 'Ongoing', 'Closed – No Case', 'Closed – Case Created'].map(status => {
                const colors = getStatusColor(status);
                return (
                  <div key={status} className="flex items-center space-x-1">
                    <div className={`w-4 h-4 rounded ${colors.bg} border ${colors.border}`}></div>
                    <span className="text-gray-600">{status}</span>
                  </div>
                );
              })}
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-0.5 h-4 bg-red-500"></div>
              <span className="text-gray-600">Today</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-1 h-4 bg-orange-500"></div>
              <span className="text-gray-600">Deadline</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-1 h-4 bg-red-600"></div>
              <span className="text-gray-600">Overdue Deadline</span>
            </div>
            <div className="flex items-center space-x-2">
              <AlertCircle className="w-4 h-4 text-red-500" />
              <span className="text-gray-600">Overdue Case</span>
            </div>
          </div>
        </div>
      </div>

      {/* Selected Cases Summary */}
      {selectedCases.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-blue-900">
              {selectedCases.length} case{selectedCases.length !== 1 ? 's' : ''} selected
            </span>
            <button
              onClick={() => setSelectedCases([])}
              className="text-sm text-blue-600 hover:text-blue-700"
            >
              Clear Selection
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default GanttChart;
